import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import {
  LinkIcon,
  UsersIcon,
  ArrowLeftIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';
import { useFamilyStore } from '../../store/familyStore';
import api from '../../api/client';

interface LinkInvitation {
  id: string;
  from_family_id: string;
  from_family_name: string;
  invite_code: string;
  expires_at: string;
  is_used: boolean;
}

export default function LinkFamily() {
  const { inviteCode } = useParams<{ inviteCode: string }>();
  const navigate = useNavigate();
  const { families, currentFamily, fetchFamilies } = useFamilyStore();
  const [invitation, setInvitation] = useState<LinkInvitation | null>(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [selectedFamilyId, setSelectedFamilyId] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (inviteCode) {
      loadInvitation();
    } else {
      setError('No invite code provided');
      setLoading(false);
    }
  }, [inviteCode]);

  useEffect(() => {
    fetchFamilies().catch(console.error);
  }, []);

  useEffect(() => {
    // Auto-select current family if available
    if (currentFamily && !selectedFamilyId) {
      setSelectedFamilyId(currentFamily.id);
    } else if (families.length > 0 && !selectedFamilyId) {
      setSelectedFamilyId(families[0].id);
    }
  }, [currentFamily, families, selectedFamilyId]);

  const loadInvitation = async () => {
    console.log('[LinkFamily] Loading invitation for code:', inviteCode);
    try {
      const response = await api.get(`/family-links/link-invitation/${inviteCode}`);
      console.log('[LinkFamily] Invitation loaded:', response.data);
      setInvitation(response.data);
      setLoading(false);
    } catch (error: any) {
      console.error('[LinkFamily] Error loading invitation:', error);
      setError(error.response?.data?.detail || 'Invalid or expired invitation');
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    if (!selectedFamilyId || !inviteCode) return;
    
    setAccepting(true);
    try {
      await api.post(`/family-links/accept-link/${inviteCode}?target_family_id=${selectedFamilyId}`);
      toast.success('Families linked successfully!');
      navigate('/families');
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to link families');
    } finally {
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <div className="page-content flex items-center justify-center min-h-[50vh]">
        <div className="w-10 h-10 border-2 border-primary-500/30 border-t-primary-500 rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-content">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card text-center py-12"
        >
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-500/20 flex items-center justify-center">
            <LinkIcon className="w-8 h-8 text-red-400" />
          </div>
          <h2 className="text-xl font-bold text-white mb-2">Invalid Invitation</h2>
          <p className="text-surface-400 mb-6">{error}</p>
          <button onClick={() => navigate('/families')} className="btn-primary">
            Go to Families
          </button>
        </motion.div>
      </div>
    );
  }

  // Filter out the inviting family from choices
  const eligibleFamilies = families.filter(f => 
    f.id !== invitation?.from_family_id && 
    (f.my_role === 'owner' || f.my_role === 'admin')
  );

  return (
    <div className="page-content">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button onClick={() => navigate('/families')} className="btn-icon">
          <ArrowLeftIcon className="w-6 h-6" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-white">Link Families</h1>
          <p className="text-surface-400 text-sm">Connect your families together</p>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="card max-w-lg mx-auto"
      >
        {/* Invitation Info */}
        <div className="flex items-center gap-4 mb-6 p-4 rounded-xl bg-primary-500/10 border border-primary-500/30">
          <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center">
            <UsersIcon className="w-7 h-7 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-white">{invitation?.from_family_name}</h3>
            <p className="text-sm text-surface-400">wants to connect with your family</p>
          </div>
        </div>

        <p className="text-surface-300 mb-6">
          When families are linked, you can share passwords, wishlists, and other resources between them. 
          Members from linked families can see and interact with shared items.
        </p>

        {eligibleFamilies.length === 0 ? (
          <div className="text-center py-6">
            <p className="text-surface-400 mb-4">
              You need to be an admin or owner of a family to accept this invitation.
            </p>
            <button onClick={() => navigate('/families')} className="btn-secondary">
              Go to Families
            </button>
          </div>
        ) : (
          <>
            <div className="input-group mb-6">
              <label className="input-label">Select your family to link</label>
              <select
                className="input"
                value={selectedFamilyId}
                onChange={(e) => setSelectedFamilyId(e.target.value)}
              >
                {eligibleFamilies.map((family) => (
                  <option key={family.id} value={family.id}>
                    {family.name} ({family.my_role})
                  </option>
                ))}
              </select>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => navigate('/families')}
                className="btn-secondary flex-1"
              >
                Cancel
              </button>
              <button
                onClick={handleAccept}
                disabled={accepting || !selectedFamilyId}
                className="btn-primary flex-1 flex items-center justify-center gap-2"
              >
                {accepting ? (
                  'Linking...'
                ) : (
                  <>
                    <CheckIcon className="w-5 h-5" />
                    Link Families
                  </>
                )}
              </button>
            </div>
          </>
        )}
      </motion.div>
    </div>
  );
}
